import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { HeroPublish } from "@/components/lab/HeroPublish";
import { IdeaCard } from "@/components/lab/IdeaCard";
import { RankingSidebar } from "@/components/lab/RankingSidebar";
import { listIdeas, toggleVote, listMyVotes, LAB_CATEGORIES } from "@/lib/lab.functions";
import { useCurrentUser } from "@/hooks/useCurrentUser";

export const Route = createFileRoute("/lab/")({ component: LabIndex });

const FILTERS = [
  { value: "trending", label: "Em alta" },
  { value: "recent", label: "Recentes" },
  { value: "top", label: "Mais votadas" },
  { value: "in_progress", label: "Em dev" },
  { value: "launched", label: "Lançadas" },
] as const;

function LabIndex() {
  const { isAuthenticated } = useCurrentUser();
  const [filter, setFilter] = useState<string>("trending");
  const [category, setCategory] = useState<string>("all");
  const [search, setSearch] = useState("");
  const qc = useQueryClient();

  const list = useServerFn(listIdeas);
  const vote = useServerFn(toggleVote);
  const myVotesFn = useServerFn(listMyVotes);

  const ideasQ = useQuery({
    queryKey: ["lab-ideas", filter, category, search],
    queryFn: () => list({ data: { filter: filter as any, category, search } }),
  });
  const myVotesQ = useQuery({
    queryKey: ["lab-my-votes"],
    queryFn: () => myVotesFn(),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
  const votedSet = useMemo(() => new Set(myVotesQ.data ?? []), [myVotesQ.data]);

  const voteMut = useMutation({
    mutationFn: (id: string) => vote({ data: { ideaId: id } }),
    onMutate: async (id: string) => {
      await qc.cancelQueries({ queryKey: ["lab-ideas"] });
      await qc.cancelQueries({ queryKey: ["lab-my-votes"] });
      const prevVotes = qc.getQueryData<string[]>(["lab-my-votes"]) ?? [];
      const isVoted = prevVotes.includes(id);
      const nextVotes = isVoted ? prevVotes.filter((v) => v !== id) : [...prevVotes, id];
      qc.setQueryData(["lab-my-votes"], nextVotes);
      qc.setQueriesData<any[]>({ queryKey: ["lab-ideas"] }, (rows) =>
        (rows ?? []).map((r) =>
          r.id === id ? { ...r, vote_count: Math.max(0, r.vote_count + (isVoted ? -1 : 1)) } : r,
        ),
      );
      return { prevVotes };
    },
    onError: (e: Error, _id, ctx) => {
      if (ctx?.prevVotes) qc.setQueryData(["lab-my-votes"], ctx.prevVotes);
      qc.invalidateQueries({ queryKey: ["lab-ideas"] });
      toast.error(e.message);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["lab-ideas"] });
      qc.invalidateQueries({ queryKey: ["lab-my-votes"] });
    },
  });

  const ideas = (ideasQ.data ?? []).map((i) => ({ ...i, voted: votedSet.has(i.id) }));

  return (
    <div>
      <HeroPublish />

      <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
        <div className="min-w-0">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1.5">
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setFilter(f.value)}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                    filter === f.value
                      ? "border-foreground bg-foreground text-background"
                      : "border-[color:var(--hairline)] bg-surface text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="rounded-full border border-[color:var(--hairline)] bg-surface px-3 py-1.5 text-xs font-medium focus:border-primary focus:outline-none"
              >
                <option value="all">Todas as categorias</option>
                {LAB_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar"
                  className="w-40 rounded-full border border-[color:var(--hairline)] bg-surface py-1.5 pl-8 pr-3 text-xs focus:border-primary focus:outline-none"
                />
              </div>
            </div>
          </div>

          {ideasQ.isLoading ? (
            <div className="grid gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-24 animate-pulse rounded-2xl bg-surface" />
              ))}
            </div>
          ) : ideas.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[color:var(--hairline)] p-10 text-center text-sm text-muted-foreground">
              Nenhuma ideia por aqui ainda. Seja o primeiro a publicar.
            </div>
          ) : (
            <div className="grid gap-3">
              {ideas.map((idea) => (
                <IdeaCard
                  key={idea.id}
                  idea={idea}
                  voting={voteMut.isPending}
                  onVote={() => {
                    if (!isAuthenticated) { toast.info("Entre para votar"); return; }
                    voteMut.mutate(idea.id);
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="lg:sticky lg:top-20 lg:self-start">
          <RankingSidebar limit={10} />
        </div>
      </div>
    </div>
  );
}
